import { Box, DrawerBody, DrawerHeader, IconButton, Icon, InputRightElement } from '@chakra-ui/react';
import { Formik, Form } from 'formik';
import React, { useRef } from 'react';
import { useTranslation } from 'react-i18next';

import { IconView, IconViewOff } from 'assets/icons';
import Button from 'components/Button';
import { DrawerFixedButton } from 'components/Drawer';
import { FormikInput } from 'components/Formik';
import PasswordStrengthChecker from 'components/PasswordStrengthChecker';
import { useChangePassword } from 'hooks/profile/useChangePassword';

import { changePasswordFormValidationSchema, initialChangePasswordFormData } from './schema';

interface IChangePasswordProps {
  onClose?: () => void;
}

const ChangePassword: React.FC<IChangePasswordProps> = ({ onClose }) => {
  const { t } = useTranslation(['profile', 'common']);

  const passwordInputRef = useRef<any>(null);
  const { status, mutateAsync } = useChangePassword();

  const handleClickShowPassword = (
    setFieldValue: (field: string, value: any, shouldValidate?: boolean) => void,
    showPassword: boolean,
    showPasswordKey: string
  ) => {
    setFieldValue(showPasswordKey, !showPassword);
  };

  const handleFormSubmit = async (values: typeof initialChangePasswordFormData) => {
    mutateAsync(values).then(() => {
      if (onClose) {
        onClose();
      }
    });
  };

  return (
    <Box>
      <DrawerHeader>{t('profile:changePassword')}</DrawerHeader>
      <DrawerBody>
        <Formik
          initialValues={initialChangePasswordFormData}
          validationSchema={changePasswordFormValidationSchema}
          onSubmit={handleFormSubmit}
        >
          {({ values, setFieldValue }) => (
            <Box minHeight="540px">
              <Form noValidate>
                <FormikInput
                  type={values.showPasswordOld ? 'text' : 'password'}
                  name="old_password"
                  label={t('common:password')}
                  formControlProps={{ isRequired: true }}
                  inputElement={
                    <InputRightElement width="4.5rem">
                      <IconButton
                        aria-label="toggle view password"
                        size="sm"
                        onClick={() =>
                          handleClickShowPassword(setFieldValue, values.showPasswordOld, 'showPasswordOld')
                        }
                        icon={values.showPasswordOld ? <Icon as={IconViewOff} /> : <Icon as={IconView} />}
                      ></IconButton>
                    </InputRightElement>
                  }
                />
                <Box position="relative">
                  <FormikInput
                    type={values.showPasswordNew ? 'text' : 'password'}
                    name="new_password"
                    label={t('profile:newPassword')}
                    formControlProps={{ isRequired: true }}
                    inputProps={{ ref: passwordInputRef }}
                    inputElement={
                      <InputRightElement width="4.5rem">
                        <IconButton
                          aria-label="toggle view password"
                          size="sm"
                          onClick={() =>
                            handleClickShowPassword(setFieldValue, values.showPasswordNew, 'showPasswordNew')
                          }
                          icon={values.showPasswordNew ? <Icon as={IconViewOff} /> : <Icon as={IconView} />}
                        ></IconButton>
                      </InputRightElement>
                    }
                  />
                  <PasswordStrengthChecker
                    passwordValue={values.new_password}
                    listProps={{
                      visibility: passwordInputRef?.current === document.activeElement ? 'visible' : 'hidden',
                      opacity: passwordInputRef?.current === document.activeElement ? 100 : 0,
                    }}
                  />
                </Box>
                <FormikInput
                  type={values.showPasswordConfirm ? 'text' : 'password'}
                  name="confirm_password"
                  label={t('profile:confirmNewPassword')}
                  formControlProps={{ isRequired: true }}
                  inputElement={
                    <InputRightElement width="4.5rem">
                      <IconButton
                        aria-label="toggle view password"
                        size="sm"
                        onClick={() =>
                          handleClickShowPassword(setFieldValue, values.showPasswordConfirm, 'showPasswordConfirm')
                        }
                        icon={values.showPasswordConfirm ? <Icon as={IconViewOff} /> : <Icon as={IconView} />}
                      />
                    </InputRightElement>
                  }
                />
                <DrawerFixedButton>
                  <Button type="submit" colorScheme="secondary" width="100%" size="lg" isLoading={status === 'loading'}>
                    {t('profile:changePassword')}
                  </Button>
                </DrawerFixedButton>
              </Form>
            </Box>
          )}
        </Formik>
      </DrawerBody>
    </Box>
  );
};

export default ChangePassword;
